import React, { useEffect, useMemo, useState } from "react";
import { ErrorMessage, Field, Formik, Form } from "formik";
import { useFormikContext } from "formik";
import * as Yup from "yup";
import { NavLink, useNavigate } from "react-router-dom";
import { routes } from "../../constants/routes";
import { Box } from "@mui/material";
import LoginBcgImage from "../../assets/images/bg-login.png";
import BgForgotImage from "../../assets/images/forgot-bg.png";
import icons from "../../assets/icons/icons";
import "../login/login.scss";
import "./request-access.scss";
import backIcon from "../../assets/icons/back.svg";

import SearchableFreeSoloAutoSelect from "../../common/components/searchable-freesolo-auto-select-component/searchable-freesolo-auto-select-component";
import DisplayVersion from "../display-version/display-version";
import { CountryCodeWithPhoneNumber, Welcome } from "../../common/components";
import { useSetLoading } from "../../common/hooks/appearance";
import { isOnlyCountryCode } from "../../utils/utils";
import phoneValidation from "../../common/components/phone-number-input-component/phone-validation";
import { emailValidationRegex } from "../../constants/regexStrings";
import { getCompanyNamesInRequestAccess, requestAccess } from "../../api/login";
import { OptionType } from "../../common/components/searchable-freesolo-auto-select-component/searchable-auto-select-props";
const RequestAccess = () => {
  const [message, setMessage] = useState<{
    message: string;
    types: string;
  } | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExistingCompany, setIsExistingCompany] = useState<boolean>(false);
  const [companyNames, setCompanyNames] = useState<any>([]);
  const navigate = useNavigate();

  const initialValues = {
    companyName: { id: "", companyName: "" },
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    noOfUsers: "",
  };

  const validationSchema = Yup.object({
    firstName: Yup.string().max(50).required("First name is required"),
    lastName: Yup.string().max(50).required("Last name is required"),
    email: Yup.string()
      .matches(emailValidationRegex, "Please enter a valid Email address")
      .required("Email is required"),
    companyName: Yup.object().required("Company name is required"),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .test(
        "valid-phone-check",
        "Please enter a valid phone number",
        function (value) {
          if (!value) return false;

          if (isOnlyCountryCode(value)) {
            return this.createError({
              message: "Phone number is required",
            });
          }

          if (!phoneValidation(value)) {
            return this.createError({
              message: "Please enter a valid phone number",
            });
          }

          return true;
        }
      ),
    noOfUsers: Yup.string().when("companyName", {
      is: (val: any) => {
        return !companyNames.some(
          (company: OptionType) => company.id === Number(val?.id)
        );
      },
      then: (schema) =>
        schema
          .required("Users is required")
          .matches(/^\d+$/, "Must be a number")
          .min(1, "Must be at least 1 user"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
  const onSubmit = async (values: typeof initialValues) => {
    setIsLoading(true);
    try {
      const { noOfUsers, ...restValues } = values;
      const payload = isExistingCompany
        ? { ...restValues, isExistingCompany }
        : { ...values, isExistingCompany };
      const response: any = await requestAccess(payload);
      if (response?.data?.data?.message) {
        setIsSubmitted(true);
      }
      setIsLoading(false);
    } catch (error: any) {
      setMessage({
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong!",
        types: "error",
      });
      setIsLoading(false);
    }
  };
  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  useEffect(() => {
    const fetchCompanyNames = async () => {
      const data = await getCompanyNamesInRequestAccess();
      setCompanyNames(data.data.data);
    };
    fetchCompanyNames();
  }, []);
  return (
    <div
      className="login-page-wrapper request-access-wrapper"
      style={{
        backgroundImage: `url(${BgForgotImage}), url(${LoginBcgImage})`,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "center bottom, center bottom",
        backgroundSize: "100% 40%, 100% 40%",
      }}
    >
      <div className="login-card-wrapper">
        <div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize={true}
          >
            {({
              setFieldValue,
              values,
              setFieldTouched,
              dirty,
              isValid,
              errors,
              touched,
              handleBlur,
            }) => {
              return (
                <Form className="loginForm">
                  {isSubmitted ? (
                    <Box className="forgotpassword-wrapper">
                      <div className="back-icon-wraper">
                        <NavLink to={routes.LOGIN}>
                          <img src={backIcon} alt="back" />
                          <span>Back to Login</span>
                        </NavLink>
                      </div>
                      <Box className="request-access-success-wrapper">
                        <Box sx={{ position: "relative" }}>
                          <img
                            src={icons.ContactUsLike}
                            style={{
                              position: "absolute",
                              top: "65px",
                              left: "115px",
                            }}
                          />
                          <img
                            src={icons.ContactUsStars}
                            className="stars-image"
                          />
                        </Box>
                        <p className="su">
                          Thank you! Your request has been submitted. Our team
                          will contact you shortly.
                        </p>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <h2>Request Access</h2>
                      <div className="request-access-formBody">
                        <div className="field">
                          <SearchableFreeSoloAutoSelect
                            fieldName="companyName"
                            options={companyNames}
                            onChange={(value: OptionType | string) => {
                              setFieldValue("companyName", value);
                              const selectedCompanyName =
                                typeof value === "string"
                                  ? value
                                  : value?.companyName;
                              const companyExists = companyNames.some(
                                (c: any) =>
                                  typeof c === "object" &&
                                  "companyName" in c &&
                                  c.companyName === selectedCompanyName
                              );
                              setIsExistingCompany(companyExists);
                            }}
                            value={values.companyName}
                            placeholder="Company Name"
                            isShowError={
                              !!(errors.companyName && touched.companyName)
                            }
                            handleBlur={handleBlur}
                          />
                        </div>
                        <>
                          <Box className="request-access-name-wrapper" gap={4}>
                            <div className="field">
                              <Field
                                type="text"
                                name="firstName"
                                placeholder="First Name"
                                maxLength={50}
                                onChange={(e: any) => {
                                  const lettersOnly = e.target.value.replace(
                                    /[^a-zA-Z0-9 ]/g,
                                    ""
                                  );
                                  setFieldValue("firstName", lettersOnly);
                                }}
                                disabled={values.companyName?.id || values.companyName?.companyName ? false : true}
                              />
                              <ErrorMessage
                                name="firstName"
                                component="div"
                                className="errorMessage"
                              />
                            </div>
                            <div className="field">
                              <Field
                                type="text"
                                name="lastName"
                                placeholder="Last Name "
                                maxLength={50}
                                onChange={(e: any) => {
                                  const lettersOnly = e.target.value.replace(
                                    /[^a-zA-Z0-9 ]/g,
                                    ""
                                  );
                                  setFieldValue("lastName", lettersOnly);
                                }}
                                disabled={values.companyName?.id || values.companyName?.companyName ? false : true}
                              />
                              <ErrorMessage
                                name="lastName"
                                component="div"
                                className="errorMessage"
                              />
                            </div>
                          </Box>
                          <Box className="request-access-name-wrapper" gap={4}>
                            {!isExistingCompany && (
                              <div className="field">
                                <Field
                                  type="text"
                                  name="noOfUsers"
                                  placeholder="No. of user"
                                  onChange={(e: any) => {
                                    const numbersOnly = e.target.value.replace(
                                      /[^0-9]/g,
                                      ""
                                    );
                                    setFieldValue("noOfUsers", numbersOnly);
                                  }}
                                  disabled={values.companyName?.id || values.companyName?.companyName ? false : true}
                                />
                                <ErrorMessage
                                  name="noOfUsers"
                                  component="div"
                                  className="errorMessage"
                                />
                              </div>
                            )}
                            <div className="field">
                              <Field
                                type="email"
                                name="email"
                                placeholder="Email"
                                disabled={values.companyName?.id || values.companyName?.companyName ? false : true}
                              />
                              <ErrorMessage
                                name="email"
                                component="div"
                                className="errorMessage"
                              />
                            </div>
                          </Box>
                          <div className="field">
                            <CountryCodeWithPhoneNumber
                              fieldName="phoneNumber"
                              placeholder="Enter phone number"
                              onChange={(value: string, country: any) => {
                                const countryCode = country?.dialCode;

                                if (!countryCode) {
                                  console.warn(
                                    "Missing dialCode in country object"
                                  );
                                  return;
                                }

                                const numberOnly = value.slice(
                                  countryCode.length
                                );
                                const formatted = `+${countryCode} ${numberOnly}`;

                                setFieldValue("phoneNumber", formatted);
                              }}
                              onBlur={() => {
                                setFieldTouched("phoneNumber", true);
                              }}
                              value={values.phoneNumber}
                              defaultCountry="us"
                              disabled={values.companyName?.id || values.companyName?.companyName ? false : true}
                              // countryCodeEditable
                            />
                            <ErrorMessage
                              name="phoneNumber"
                              component="div"
                              className="errorMessage"
                            />
                          </div>
                        </>
                        <Box className="request-access-button-wrapper" gap={1}>
                          <button
                            type="button"
                            className="cancelButton"
                            onClick={() => {
                              navigate(routes.LOGIN);
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="submitButton"
                            disabled={!dirty || !isValid}
                          >
                            {isLoading ? "Sending..." : "Submit"}
                          </button>
                        </Box>
                        {message?.message && (
                          <div
                            className={`${
                              message?.types === "error"
                                ? "login-error-message"
                                : "login-success-message"
                            } message-container`}
                            style={{ marginTop: "10px", textAlign: "center" }}
                          >
                            {message?.message}
                          </div>
                        )}
                        <div className="version-info">
                          <DisplayVersion />
                        </div>
                      </div>
                    </>
                  )}
                </Form>
              );
            }}
          </Formik>
        </div>
        <div className="right-content-wraper">
          <Welcome />
        </div>
      </div>
    </div>
  );
};
export default RequestAccess;
